import React, { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import BaseModal from "@components/modals/BaseModal";
import { useDataStore } from "@store/user_org";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import { useOrgClosures } from "@utils/closedDates";
import type { OrgClosure } from "../../../types/org";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { showNotification } from "context/notification.service";

/** Parse an ISO date (YYYY-MM-DD) into a UTC Date (TZ-safe). */
function isoToUtcDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Short French label (no weekday) for range bounds. */
function formatShort(iso: string): string {
  return isoToUtcDate(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Annual closures hide the year (a recurring jour férié has no fixed year). */
function formatAnnual(iso: string): string {
  return isoToUtcDate(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    timeZone: "UTC",
  });
}

function formatClosure(c: OrgClosure): string {
  const fmt = c.annual ? formatAnnual : formatShort;
  const end = c.end && c.end > c.start ? c.end : null;
  return end ? `Du ${fmt(c.start)} au ${fmt(end)}` : fmt(c.start);
}

/**
 * Manage the org-level garage closures (Paramètres → Dispo atelier).
 *
 * A closure either blocks the online booking only (default — the atelier
 * planning stays normal) or fully closes the garage ("Fermeture totale" — the
 * planning is shown closed/red too). Each can repeat every year. Stored in
 * org config as `booking_closures`; the legacy flat `booking_closed_dates` is
 * retired on first save.
 */
export function ClosureDates() {
  const orgData = useDataStore((s) => s.orgData);
  const updateOrgData = useDataStore((s) => s.updateOrgData);
  const selectedDate = useDispoAtelierStore((s) => s.selectedDate);
  const closures = useOrgClosures();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [start, setStart] = useState<string>(selectedDate);
  const [end, setEnd] = useState<string>("");
  const [full, setFull] = useState(false);
  const [annual, setAnnual] = useState(false);
  const [saving, setSaving] = useState(false);
  // Folded by default: the closures are set once and then read rarely, while the
  // chips they unfold into can run several rows deep and push the rest of the page down.
  const [expanded, setExpanded] = useState(false);

  const openCreate = () => {
    setEditingId(null);
    setStart(selectedDate);
    setEnd("");
    setFull(false);
    setAnnual(false);
    setShowModal(true);
  };

  const openEdit = (c: OrgClosure) => {
    setEditingId(c.id);
    setStart(c.start);
    setEnd(c.end && c.end > c.start ? c.end : "");
    setFull(!!c.full);
    setAnnual(!!c.annual);
    setShowModal(true);
  };

  const persist = async (next: OrgClosure[]) => {
    if (!orgData) return;
    setSaving(true);
    try {
      // Persist structured closures and retire the legacy flat list.
      await updateOrgData(orgData.id, {
        config: { booking_closures: next, booking_closed_dates: [] },
      });
    } catch {
      showNotification({
        message: "Échec de l'enregistrement des fermetures",
        type: "error",
      });
    }
    setSaving(false);
  };

  const saveClosure = async () => {
    if (!start) return;
    const effectiveEnd = end && end >= start ? end : undefined;
    if (editingId) {
      const next = closures.map((c) =>
        c.id === editingId ? { ...c, start, end: effectiveEnd, full, annual } : c,
      );
      await persist(next);
      showNotification({ message: "Fermeture modifiée", type: "success" });
    } else {
      const closure: OrgClosure = {
        id: `cl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        start,
        end: effectiveEnd,
        full,
        annual,
      };
      await persist([...closures, closure]);
      showNotification({
        message: full ? "Fermeture du garage ajoutée" : "Période de blocage résa ajoutée",
        type: "success",
      });
    }
    setShowModal(false);
  };

  const removeClosure = async (id: string) => {
    await persist(closures.filter((c) => c.id !== id));
    if (editingId === id) setShowModal(false);
  };

  return (
    <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1 max-w-700">
      {/* Header doubles as the toggle; the count stays readable while folded. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex flex-row items-center gap-2 text-left"
      >
        <div className="flex flex-col">
          <div className="text-h3 font-semibold text-black">Fermetures &amp; blocages</div>
          <div className="text-h4 text-neutral-400">
            {closures.length > 0
              ? `${closures.length} période${closures.length > 1 ? "s" : ""} configurée${closures.length > 1 ? "s" : ""}`
              : "Aucune fermeture programmée"}
          </div>
        </div>
        <div
          className="ml-auto flex items-center justify-center transition-transform"
          style={{ transform: expanded ? "rotate(180deg)" : "none" }}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#A1A1A1" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </div>
      </button>

      {expanded && (
        <>
      <div className="text-h4 text-neutral-400">
        Bloquez une journée ou une période. Par défaut, seule la réservation en
        ligne est bloquée (votre planning atelier reste normal). Activez
        «&nbsp;Fermeture totale&nbsp;» pour un vrai jour de fermeture (le planning
        passe alors en rouge). «&nbsp;Tous les ans&nbsp;» convient aux jours fériés.
      </div>

      {/* Add a closure (opens the modal) */}
      <div>
        <Button label="Ajouter une fermeture" onClick={openCreate} variant="small" />
      </div>

      {/* Current closures — click a chip to edit */}
      {closures.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span className="text-h4 font-medium text-neutral-500">Périodes configurées</span>
          <div className="flex flex-row flex-wrap gap-2">
            {closures.map((c) => {
              const bg = c.full ? "#FEE2E2" : "#FEF3C7";
              const border = c.full ? "#FECACA" : "#FDE68A";
              const color = c.full ? "#DC2626" : "#B45309";
              return (
                <div
                  key={c.id}
                  className="flex flex-row items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg cursor-pointer transition-opacity hover:opacity-80"
                  style={{ background: bg, border: `1px solid ${border}` }}
                  onClick={() => openEdit(c)}
                  title="Modifier"
                >
                  <div className="flex flex-col">
                    <span className="text-h4 font-medium capitalize" style={{ color }}>
                      {formatClosure(c)}
                    </span>
                    <span className="text-h6 font-medium" style={{ color, opacity: 0.8 }}>
                      {c.full ? "Garage fermé" : "Résa en ligne bloquée"}
                      {c.annual ? " · tous les ans" : ""}
                    </span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeClosure(c.id); }}
                    disabled={saving}
                    title="Supprimer"
                    className="flex items-center justify-center rounded-md transition-colors"
                    style={{ width: 22, height: 22 }}
                  >
                    <Icon1 name="close" size={10} color={color} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <span className="text-h4 text-neutral-300">Aucune fermeture programmée.</span>
      )}
        </>
      )}

      {/* Create / edit modal */}
      {showModal && (
        <BaseModal
          onClose={() => setShowModal(false)}
          title={editingId ? "Modifier la fermeture" : "Ajouter une fermeture"}
          startIcon={<Icon1 name={editingId ? "sett-dispo" : "plus"} size={20} />}
          width="max-w-600"
        >
          <div className="flex flex-col p-5" style={{ gap: 16 }}>
            <div className="flex flex-row items-end gap-3 flex-wrap">
              <div className="flex flex-col gap-1">
                <span className="text-h4 font-medium text-neutral-500">Du</span>
                <input
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-black focus:outline-none focus:border-neutral-400"
                  style={{ fontFamily: "Poppins" }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-h4 font-medium text-neutral-500">Au (optionnel)</span>
                <input
                  type="date"
                  value={end}
                  min={start || undefined}
                  onChange={(e) => setEnd(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-black focus:outline-none focus:border-neutral-400"
                  style={{ fontFamily: "Poppins" }}
                />
              </div>
            </div>

            <SwitchBrick
              checked={full}
              onChange={setFull}
              title="Fermeture totale du garage"
              description="Planning atelier en rouge (sinon : résa en ligne uniquement)"
            />
            <SwitchBrick
              checked={annual}
              onChange={setAnnual}
              title="Tous les ans"
              description="Se répète chaque année (jour férié)"
            />

            <div className="flex flex-row gap-2 pt-2 justify-end">
              <SecondaryButton label="Annuler" onClick={() => setShowModal(false)} variant="small" />
              <Button
                label={saving ? "Enregistrement..." : editingId ? "Enregistrer" : "Ajouter"}
                onClick={saveClosure}
                disabled={saving || !start}
                variant="small"
              />
            </div>
          </div>
        </BaseModal>
      )}
    </div>
  );
}
